import { isEmpty } from "lodash";
import * as PDFJS from "pdfjs-dist";
import Tiff from "tiff.js";
import ApiService from "../ApiService";
import moment from "moment";

export default class ImageUtils {
  static printAll = async (patientDetails, uploadedFileObjects, referralDetails) => {
    if (isEmpty(uploadedFileObjects)) return;

    const filename =
      patientDetails.patient.full_name.replace(/\s+/g, "_") +
      "-" +
      moment().format("YYYY-MM-DD") +
      ".pdf";

    try {
      const referralUuid = referralDetails.referral.id;

      // Call API to get the file URL
      const fileUrl = await ApiService.mergeReferralDocuments(referralUuid, {
        params: { format: "pdf", filename: filename },
      });

      // Open in new window and print
      const printWindow = window.open(fileUrl);
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (err) {
      console.error("Error in printAll:", err);
    }
  };

  // ----------- Core Dispatcher ----------- //
  static async processDocuments(documents, doc, resolveVal, filename) {
    for (const file of documents) {
      await this.appendFile(file, doc);
    }
    this.generateFile(doc, resolveVal, filename);
  }

  static async appendFile(file, doc) {
    if (file.type === "application/pdf") {
      return this.appendPdf(file, doc);
    } else if (file.type.includes("tif")) {
      return this.appendTiff(file, doc);
    } else {
      return this.appendImage(file, doc);
    }
  }

  // ----------- PDF Handling ----------- //
  static async appendPdf(file, doc) {
    try {
      const data = await file.arrayBuffer();
      const pdf = await PDFJS.getDocument({ data }).promise;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;

        doc.addPage();
        const percent = doc.internal.pageSize.getWidth() / canvas.width;
        doc.addImage(
          canvas.toDataURL("image/jpeg"),
          "JPEG",
          0,
          0,
          doc.internal.pageSize.getWidth(),
          percent * canvas.height,
          null,
          "MEDIUM"
        );
      }
    } catch (err) {
      console.error("Error appending PDF:", err);
    }
  }

  // ----------- Image Handling ----------- //
  static async appendImage(file, doc) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const pageWidth = doc.internal.pageSize.getWidth();
          const scale = pageWidth / img.width;
          canvas.width = pageWidth;
          canvas.height = img.height * scale;

          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          doc.addPage();
          doc.addImage(canvas.toDataURL("image/jpeg"), "JPEG", 0, 0, canvas.width, canvas.height);

          resolve();
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  // ----------- TIFF Handling ----------- //
  static async appendTiff(file, doc) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const tiff = new Tiff({ buffer: e.target.result });
          const canvasTiff = tiff.toCanvas();
          const img = new Image();

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const pageWidth = doc.internal.pageSize.getWidth();
            const scale = pageWidth / img.width;
            canvas.width = pageWidth;
            canvas.height = img.height * scale;

            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            doc.addPage();
            doc.addImage(canvas.toDataURL("image/jpeg"), "JPEG", 0, 0, canvas.width, canvas.height);

            resolve();
          };
          img.onerror = reject;
          img.src = canvasTiff.toDataURL("image/png");
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // ----------- File Generation ----------- //
  static generateFile(doc, resolveVal, filename) {
    try {
      const blob = doc.output("blob");
      if (!isEmpty(filename)) {
        doc.autoPrint();
        doc.output("dataurlnewwindow");
      }
      resolveVal(blob);
    } catch (err) {
      console.error("Error generating file:", err);
    }
  }

  // ----------- Legacy-compatible wrappers ----------- //
  static appendDocuments(documents, doc, resolveVal, filename) {
    this.processDocuments(documents, doc, resolveVal, filename);
  }

  static appendImages(documents, doc, resolveVal, filename) {
    this.processDocuments(documents, doc, resolveVal, filename);
  }
}
